import {
  DateField,
  useRecordContext,
  RecordContextProvider,
  NumberField,
  TabbedShowLayout,
  useTranslate,
} from "react-admin";
import Box from "@mui/material/Box";
import { formatDate, toCurrency } from "../../../utils/formatter";

const isZeroDate = (dateValue) => {
  if (!dateValue) return true;
  if (typeof dateValue !== "string") return false;
  return dateValue.startsWith("0001-01-01");
};

const pickValidDate = (dateCandidates = []) => {
  for (const dateValue of dateCandidates) {
    if (!isZeroDate(dateValue)) {
      return dateValue;
    }
  }
  return "";
};

const BadDebtInstallmentExpandList = () => {
  const record = useRecordContext();
  const t = useTranslate();
  return (
    <TabbedShowLayout
      syncWithLocation={false}
      sx={{
        m: 4,
        backgroundClip: "padding-box",
        boxShadow: (theme) => {
          return theme.components.MuiPaper
            ? theme.components.MuiPaper.styleOverrides.elevation1.boxShadow
            : undefined;
        },
        borderRadius: "4px",
        bgcolor: "background.paper",
        "& .RaTabbedShowLayout-content": {
          p: 0,
        },
        "& .RaLabeled-label": {
          display: "none",
        },
      }}
    >
      <TabbedShowLayout.Tab label="mine.label.payment">
        <Box
          component="table"
          sx={{
            width: "100%",
            // bgcolor: "rgba(0,0,0,.04)",
            // fontSize: "smaller",
            borderCollapse: "collapse",
            "& th, td": {
              padding: "6px 8px",
              border: "1px solid #dddddd",
            },
          }}
        >
          <thead>
            <tr>
              <th>{t("mine.label.sale_code")}</th>
              <th>{t("mine.label.transaction_date")}</th>
              <th>{t("mine.label.due_date")}</th>
              <th style={{ textAlign: "right" }}>{t("mine.label.initial")}</th>
              <th style={{ textAlign: "right" }}>{t("mine.label.payment")}</th>
              <th style={{ textAlign: "right" }}>
                {t("mine.label.remaining_debt")}
              </th>
            </tr>
          </thead>
          <tbody>
            {record.bad_debt_installment_items.map((item, idx) => {
              const transactionDate = pickValidDate([
                item.bad_debt_item?.sale?.date,
                item.bad_debt_item?.sale?.updated_at,
                item.bad_debt_item?.sale?.created_at,
              ]);
              const dueDate = pickValidDate([
                item.bad_debt_item?.sale?.customer_debt?.due_date,
                item.bad_debt_item?.sale?.date,
                item.bad_debt_item?.sale?.updated_at,
              ]);
              const remainValue =
                item.customer_bad_debt_item?.remain ??
                item.bad_debt_item?.sale?.customer_debt?.remain;
              const remainNumber = Number(remainValue);
              const paymentNumber = Number(item.amount);
              const initialValue =
                !Number.isNaN(remainNumber) && !Number.isNaN(paymentNumber)
                  ? remainNumber + paymentNumber
                  : null;

              return (
                <tr key={item.id}>
                  <td>{item.bad_debt_item.sale.code}</td>
                  <td>{transactionDate ? formatDate(transactionDate) : ""}</td>
                  <td>{dueDate ? formatDate(dueDate) : ""}</td>
                  <td style={{ textAlign: "right" }}>
                    {initialValue === null
                      ? ""
                      : toCurrency(false).format(initialValue)}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <RecordContextProvider value={item}>
                      <NumberField
                        source="amount"
                        sx={{
                          fontSize: "inherit",
                        }}
                      />
                    </RecordContextProvider>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {remainValue === null || remainValue === undefined || remainValue === ""
                      ? ""
                      : Number.isNaN(remainNumber)
                      ? remainValue
                      : toCurrency(false).format(remainNumber)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Box>
      </TabbedShowLayout.Tab>
      {(record.transactions?.length ?? 0) > 0 && (
        <TabbedShowLayout.Tab label={t("mine.label.journal")}>
          <Box
            component="table"
            sx={{
              width: "100%",
              // bgcolor: "rgba(0,0,0,.04)",
              // fontSize: "smaller",
              borderCollapse: "collapse",
              "& th, td": {
                padding: "6px 8px",
                border: "1px solid #dddddd",
              },
            }}
          >
            <thead>
              <tr>
                <th>{t("mine.label.coa")}</th>
                <th>{t("mine.label.coa_name")}</th>
                <th>{t("mine.label.description")}</th>
                <th style={{ textAlign: "right" }}>{t("mine.label.debit")}</th>
                <th style={{ textAlign: "right" }}>{t("mine.label.credit")}</th>
              </tr>
            </thead>
            <tbody>
              {(record.transactions ?? []).map((item, idx) => {
                return (
                  <tr key={item.id}>
                    <td>{item.coa.code}</td>
                    <td>{item.coa.name}</td>
                    <td>{item.description}</td>
                    <td style={{ textAlign: "right" }}>
                      <RecordContextProvider value={item}>
                        <NumberField
                          source="debit"
                          sx={{
                            fontSize: "inherit",
                          }}
                        />
                      </RecordContextProvider>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <RecordContextProvider value={item}>
                        <NumberField
                          source="credit"
                          sx={{
                            fontSize: "inherit",
                          }}
                        />
                      </RecordContextProvider>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Box>
        </TabbedShowLayout.Tab>
      )}
      {(record.transactions?.length ?? 0) === 0 && (
        <TabbedShowLayout.Tab label={t("mine.label.journal") + "*"}>
          <Box
            component="table"
            sx={{
              width: "100%",
              // bgcolor: "rgba(0,0,0,.04)",
              // fontSize: "smaller",
              borderCollapse: "collapse",
              "& th, td": {
                padding: "6px 8px",
                border: "1px solid #dddddd",
              },
            }}
          >
            <thead>
              <tr>
                <th>{t("mine.label.coa")}</th>
                <th>{t("mine.label.coa_name")}</th>
                <th>{t("mine.label.description")}</th>
                <th style={{ textAlign: "right" }}>{t("mine.label.debit")}</th>
                <th style={{ textAlign: "right" }}>{t("mine.label.credit")}</th>
              </tr>
            </thead>
            <tbody>
              {record.bad_debt_installment_journals.map((item, idx) => {
                return (
                  <tr key={item.id}>
                    <td>{item.chart_of_account.code}</td>
                    <td>{item.chart_of_account.name}</td>
                    <td>{item.description}</td>
                    <td style={{ textAlign: "right" }}>
                      <RecordContextProvider value={item}>
                        <NumberField
                          source="debit"
                          sx={{
                            fontSize: "inherit",
                          }}
                        />
                      </RecordContextProvider>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <RecordContextProvider value={item}>
                        <NumberField
                          source="credit"
                          sx={{
                            fontSize: "inherit",
                          }}
                        />
                      </RecordContextProvider>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Box>
        </TabbedShowLayout.Tab>
      )}
      {record.type === 3 && (
        <TabbedShowLayout.Tab label={t("mine.label.check")}>
          <Box
            component="table"
            sx={{
              width: "100%",
              // bgcolor: "rgba(0,0,0,.04)",
              // fontSize: "smaller",
              borderCollapse: "collapse",
              "& th, td": {
                padding: "6px 8px",
                border: "1px solid #dddddd",
              },
            }}
          >
            <thead>
              <tr>
                <th>{t("mine.label.check_number")}</th>
                <th>{t("mine.label.check_cashing_date")}</th>
                <th>{t("mine.label.bank_name")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{record.giro}</td>
                <td>
                  <RecordContextProvider value={record}>
                    <DateField
                      source="effective_date"
                      sx={{
                        fontSize: "inherit",
                      }}
                    />
                  </RecordContextProvider>
                </td>
                <td>{record.bank_name}</td>
              </tr>
            </tbody>
          </Box>
        </TabbedShowLayout.Tab>
      )}
    </TabbedShowLayout>
  );
};

export default BadDebtInstallmentExpandList;
